import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, IconButton, Tooltip, TextField, Menu, MenuItem, useMediaQuery, Paper } from '@mui/material';
import theme from '../../util/theme/theme';
import { Settings, Edit, Save, Star, Trash, SquarePlus } from 'lucide-react';
import { AxiosError } from 'axios';
import DynamicTable, { Column } from '../table';
import Dropdown, { DropdownOption } from '../ui/dropdown';
import ConfigureModal from './configure-modal';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import DeletePopup from './delete';
import DeletePopupMobile from './mobile/delete-mobile';
import { useUser } from '../../providers/user-provider';
import { useSettings } from '../../providers/settings-provider';
import { useToast } from '../../providers/toaster-provider';

type SimulationsPopupProps = {
    open: boolean;
    onClose: () => void;
    onSimulationSelect?: (simulation: { id: string; name: string; simulationDetails?: any[] }) => void;
    onFavoritesUpdated?: (favorites: Array<{ id: string; name: string }>) => void;
    onSimulationsUpdated?: () => void;
    onSimulationDeleted?: (simulationId: string) => void;
    isReal?: boolean;
    isRecurringProvisions?: boolean;
    isOneTimeProvisions?: boolean;
    appliedSimulationId?: string | null;
    // When set, scopes the rule-editing table to just the provisions belonging to this
    // category (null = uncategorized provisions, undefined = no scoping / show everything).
    // Used to jump straight to "configure a rule for this" instead of the full unfiltered list.
    initialCategoryId?: number | null;
};

const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 1000,
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 2,
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column' as const,
    boxSizing: 'border-box' as const,
};

const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 2,
    paddingBottom: 2,
    gap: 1,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FavoriteButton: React.FC<{ isFavorite: boolean; onClick: () => void }> = ({ isFavorite, onClick }) => {
    const [active, setActive] = useState(isFavorite);

    const starFilled = <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="var(--color-primary)" stroke="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-star-icon lucide-star"><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/></svg>;

    return (
        <IconButton onClick={() => { setActive(!active); onClick(); }} size="small">
            {active ? (
                starFilled
            ) : (
                <Star size={18} />
            )}
        </IconButton>
    );
}

const SimulationsPopup: React.FC<SimulationsPopupProps> = ({ open, onClose, onSimulationSelect, onFavoritesUpdated, onSimulationsUpdated, onSimulationDeleted, isReal = true, isRecurringProvisions = true, isOneTimeProvisions = true, appliedSimulationId, initialCategoryId }) => {
    const { user } = useUser();
    const { formatDate, formatAmount, companySettings } = useSettings();
    const { showError, showSuccess } = useToast();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const [provisionList, setProvisionList] = React.useState<Array<{ id: string | number; description: string; amount: number, startDate?: string, endDate?: string, transactionDate?: string, type: string }>>([]);
    // Sorting for the desktop "Select Simulation" table below - purely a
    // client-side re-sort of the already-loaded provisionList/tableData,
    // no server round-trip (unlike the main Provisions/Invoices pages).
    const [tableSort, setTableSort] = useState<{ column: string; direction: 'ASC' | 'DESC' }>({ column: '', direction: 'ASC' });
    const handleTableSort = (colId: string) => {
        setTableSort(prev => ({ column: colId, direction: prev.column === colId && prev.direction === 'ASC' ? 'DESC' : 'ASC' }));
    };
    const renderSortableTableHeader = (col: Column) => (
        <Box
            onClick={() => handleTableSort(col.id)}
            sx={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', cursor: 'pointer', userSelect: 'none', justifyContent: col.type === 'currency' ? 'center' : 'flex-start' }}
        >
            <Typography sx={{ fontWeight: 600, fontSize: 12 }}>{col.label}</Typography>
            <Typography
                sx={{
                    fontSize: 12,
                    ml: 0.5,
                    opacity: tableSort.column === col.id ? 0.9 : 0.3,
                    color: tableSort.column === col.id ? 'var(--color-primary)' : '#707070',
                }}
            >
                {tableSort.column === col.id ? (tableSort.direction === 'ASC' ? '▲' : '▼') : '▼'}
            </Typography>
        </Box>
    );
    const [fetchProvisions, setFetchProvisions] = useApi();
    const [simulationsList, setSimulationsList] = React.useState<Array<any>>([]);
    const [fetchSimulations, setFetchSimulations] = useApi();
    const [isRenaming, setIsRenaming] = useState<boolean>(false);
    const [renameValue, setRenameValue] = useState<string>('');
    const [actionValues, setActionValues] = useState<Record<string, string>>({});
    const [simulation, setSimulation] = useState<string>('blank simulation');
    const [openConfigureModal, setOpenConfigureModal] = useState<boolean>(false);
    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
    const [selectedMode, setSelectedMode] = useState<'defer' | 'move' | 'exclude-between'>('defer');
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [configDetails, setConfigDetails] = useState<Record<string, any>>({});
    const [saveMode, setSaveMode] = useState<'new' | 'changes'>('new');
    const [favorites, setFavorites] = useState<Set<string>>(new Set());
    const [openDeletePopup, setOpenDeletePopup] = useState<boolean>(false);
    const [simulationToDelete, setSimulationToDelete] = useState<string | null>(null);
    const [deleteSimulation, setDeleteSimulation] = useApi();
    const [selectedProvisionData, setSelectedProvisionData] = useState<{ description: string; amount: number; type: string; startDate?: string; endDate?: string; transactionDate?: string } | undefined>(undefined);
    const [createSimulation, setCreateSimulation] = useApi();
    const [updateSimulation, setUpdateSimulation] = useApi();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [renameSimulation, setRenameSimulation] = useApi();
    const [saveFavorites, setSaveFavorites] = useApi();
    const [fetchFavorites, setFetchFavorites] = useApi();
    const [summaryData, setSummaryData] = React.useState<any>(null);
    const [configureTransactions, setConfigureTransactions] = React.useState<any[]>([]);
    const [newSimulationMenuAnchor, setNewSimulationMenuAnchor] = useState<null | HTMLElement>(null);
    const [copySimulationMenuAnchor, setCopySimulationMenuAnchor] = useState<null | HTMLElement>(null);
    const [newSimulationMode, setNewSimulationMode] = useState<'sync' | 'include-all' | 'copy' | null>(null);

    useEffect(() => {
        if (!open) return;
        if (user?.entityId) {
            const { url, method } = buildApiRequestConfig(routes.transaction.summary, [{ param: 'entityId', value: user.entityId }]);
            setFetchProvisions({ url, method });
            
            // Fetch simulations
            const { url: simulationsUrl, method: simulationsMethod } = buildApiRequestConfig(routes.simulation.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
            setFetchSimulations({ url: simulationsUrl, method: simulationsMethod });

            // Fetch favorites from filter
            if (user?.guid) {
                const { url: favoritesUrl, method: favoritesMethod } = buildApiRequestConfig(routes.filter.fetchByGuid, [{ param: 'userGuid', value: user.guid }, { param: 'entityId', value: user.entityId }]);
                setFetchFavorites({ url: favoritesUrl, method: favoritesMethod });
            }
        }
        // Set default simulation name with date format
        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const year = today.getFullYear();
        setRenameValue(`sim-${day}${month}${year}`);
        // Set simulation to applied one if provided, otherwise blank
        setSimulation(appliedSimulationId || 'blank simulation');
        setActionValues({});
        setConfigDetails({});
        setSaveMode(appliedSimulationId ? 'changes' : 'new');
        // If opening without applied sim, use sync mode by default
        setNewSimulationMode(appliedSimulationId ? null : 'sync');
    }, [open, user?.entityId, user?.guid, appliedSimulationId, setFetchFavorites, setFetchProvisions, setFetchSimulations]);

    useEffect(() => {
        const { data, isLoading } = fetchProvisions;
        if (data && !isLoading) {
            // Store the original summary data for later use (e.g., getting transactions for Configure)
            setSummaryData(data);
            
            const items: Array<{ id: number | string; description: string; amount: number; startDate?: string; endDate?: string; transactionDate?: string; type: string }> = [];

            // When scoped to a single category, only provisions matter (real transactions
            // and invoices aren't category-scoped the same way, and are irrelevant to "what
            // makes up this forecast cell") - and only provisions belonging to that category show.
            const categoryFilterActive = initialCategoryId !== undefined;
            const matchesCategoryFilter = (p: any) => initialCategoryId === null ? (p.categoryId == null) : (p.categoryId === initialCategoryId);

            // Add real transactions grouped by bank
            if (!categoryFilterActive && data.realTransactionsByBank) {
                Object.entries(data.realTransactionsByBank).forEach(([bankKey, bankData]: [string, any]) => {
                    // Sum all amounts for this bank
                    const totalAmount = bankData.transactions
                        ? bankData.transactions.reduce((sum: number, tx: any) => sum + tx.amount, 0)
                        : 0;

                    // Get earliest and latest transaction dates
                    let earliestDate = '';
                    let latestDate = '';
                    if (bankData.transactions && bankData.transactions.length > 0) {
                        const dates = bankData.transactions
                            .map((tx: any) => new Date(tx.date))
                            .sort((a: Date, b: Date) => a.getTime() - b.getTime());
                        earliestDate = dates[0]?.toISOString().split('T')[0] || '';
                        latestDate = dates[dates.length - 1]?.toISOString().split('T')[0] || '';
                    }

                    items.push({
                        id: `real-${bankData.bankAccountId}`,
                        description: `Real Transactions (${bankKey.split(' ')[2]})`,
                        amount: totalAmount,
                        startDate: earliestDate,
                        endDate: latestDate,
                        type: '-'
                    });
                });
            }

            // Add invoices
            if (!categoryFilterActive && data.invoices) {
                const invoiceTransactions = data.invoices['All Invoices']?.transactions || [];
                // A provision-type invoice transaction's own `amount` gets
                // reduced (often to 0) once it's linked/settled against a
                // real payment - same reasoning as buildSumsByCategory
                // (forecast-groups.ts) - so this sums `originalAmount`
                // for those, falling back to `amount` for already-real
                // (already-paid, no provision leg) invoice transactions.
                const invoicesTotal = invoiceTransactions.reduce((sum: number, tx: any) =>
                    sum + (Number(tx.type === 'provision' ? (tx.originalAmount ?? tx.amount) : tx.amount) || 0), 0);
                items.push({
                    id: 'invoices',
                    description: 'Invoices',
                    amount: invoicesTotal,
                    startDate: '',
                    endDate: '',
                    type: '-'
                });
            }

            // Add recurring provision templates
            if (data.recurringProvisions) {
                let provisions = data.recurringProvisions['Recurring Provision Templates']?.provisions || [];
                if (categoryFilterActive) provisions = provisions.filter(matchesCategoryFilter);
                provisions.forEach((p: any) => {
                    items.push({
                        id: p.id,
                        description: p.description,
                        amount: p.amount,
                        startDate: p.startDate,
                        endDate: p.endDate,
                        type: p.type
                    });
                });
            }

            // Add one-time provisions
            if (data.oneTimeProvisions) {
                let provisions = data.oneTimeProvisions['One-Time Provisions']?.provisions || [];
                if (categoryFilterActive) provisions = provisions.filter(matchesCategoryFilter);
                const consolidateThreshold = companySettings?.settings.consolidateOneTimeThreshold ?? 5;
                
                // Check if we need to consolidate based on threshold
                if (provisions.length > consolidateThreshold) {
                    // Consolidate into single item
                    const totalAmount = provisions.reduce((sum: number, p: any) => sum + p.amount, 0);
                    items.push({
                        id: 'onetime-consolidated',
                        description: 'One-Time Provisions',
                        amount: totalAmount,
                        startDate: '',
                        endDate: '',
                        transactionDate: '',
                        type: '-'
                    });
                } else {
                    // Add individually
                    provisions.forEach((p: any) => {
                        items.push({
                            id: `onetime-${p.id}`,
                            description: p.description,
                            amount: p.amount,
                            startDate: p.startDate,
                            endDate: p.endDate,
                            transactionDate: p.transactionDate,
                            type: 'one-time'
                        });
                    });
                }
            }

            setProvisionList(items);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchProvisions, initialCategoryId]);

    useEffect(() => {
        const { data, isLoading } = fetchSimulations;
        if (data && !isLoading && Array.isArray(data)) {
            setSimulationsList(data);
            // If appliedSimulationId is set, find and select it from the fetched list
            if (appliedSimulationId && data.length > 0) {
                const appliedSim = data.find(sim => String(sim.id) === String(appliedSimulationId));
                if (appliedSim) {
                    setSimulation(String(appliedSim.id));
                    setSaveMode('changes');
                }
            }
        }
    }, [fetchSimulations, appliedSimulationId]);

    // Load favorites from filter data
    useEffect(() => {
        const { data, isLoading } = fetchFavorites;
        if (data && !isLoading) {
            // Parse favoriteSimIds from the filter data
            if (data.favoriteSimIds) {
                const favIds = typeof data.favoriteSimIds === 'string' 
                    ? data.favoriteSimIds.split(',').map((id: string) => id.trim()).filter((id: string) => id)
                    : Array.isArray(data.favoriteSimIds)
                    ? data.favoriteSimIds
                    : [];
                setFavorites(new Set(favIds.map(String)));
            }
        }
    }, [fetchFavorites]);

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            setIsRenaming(false);
            setRenameValue('');
            setActionValues({});
            setIsSaving(false);
            setConfigDetails({});
            setSaveMode('new');
            setOpenDeletePopup(false);
            setSimulationToDelete(null);
            setSelectedProvisionData(undefined);
            setOpenConfigureModal(false);
            setSelectedRowId(null);
            setSelectedMode('defer');
            setSummaryData(null);
            setConfigureTransactions([]);
            setNewSimulationMenuAnchor(null);
            setCopySimulationMenuAnchor(null);
            setNewSimulationMode(null);
        }
    }, [open]);

    // Initialize blank simulation with exclusions based on toggle states
    useEffect(() => {
        if (simulation === 'blank simulation' && provisionList.length > 0 && open && newSimulationMode === 'sync') {
            const newActionValues: Record<string, string> = {};
            
            // Auto-exclude items based on toggle states
            provisionList.forEach((provision) => {
                const id = String(provision.id);
                if (id.startsWith('real-') && !isReal) {
                    newActionValues[id] = 'exclude';
                } else if ((id.startsWith('onetime-') || id === 'onetime-consolidated') && !isOneTimeProvisions) {
                    newActionValues[id] = 'exclude';
                } else if (!id.startsWith('real-') && !id.startsWith('onetime-') && id !== 'onetime-consolidated' && id !== 'invoices' && !isRecurringProvisions) {
                    newActionValues[id] = 'exclude';
                }
            });
            
            setActionValues(newActionValues);
        }
    }, [simulation, provisionList, isReal, isRecurringProvisions, isOneTimeProvisions, open, newSimulationMode]);

    // Load selected simulation data
    useEffect(() => {
        // Skip loading if blank simulation or no simulations available
        if (!simulation || simulation === 'blank simulation' || simulationsList.length === 0) {
            return;
        }

        const selectedSimulation = simulationsList.find(sim => String(sim.id) === String(simulation));
        if (selectedSimulation) {
            // Set the simulation name for rename
            setRenameValue(selectedSimulation.name || '');
            setSaveMode('changes');

            // Parse simulation details and populate actionValues and configDetails
            const newActionValues: Record<string, string> = {};
            const newConfigDetails: Record<string, any> = {};

            selectedSimulation.simulationDetails?.forEach((detail: any) => {
                const rowId = detail.details?.id;
                if (rowId) {
                    newActionValues[rowId] = detail.mode;

                    if (detail.mode === 'exclude-between' && detail.details?.start && detail.details?.end) {
                        newConfigDetails[rowId] = {
                            start: detail.details.start,
                            end: detail.details.end
                        };
                    } else if (detail.mode === 'defer') {
                        newConfigDetails[rowId] = {};
                        if (detail.details?.start && detail.details?.end) {
                            newConfigDetails[rowId].start = detail.details.start;
                            newConfigDetails[rowId].end = detail.details.end;
                        }
                        if (detail.details?.defer) {
                            newConfigDetails[rowId].defer = detail.details.defer;
                        }
                    } else if (detail.mode === 'move' && detail.details?.movesByRowId) {
                        newConfigDetails[rowId] = {
                            movesByRowId: detail.details.movesByRowId
                        };
                    } else if (detail.mode === 'move' && detail.details?.moveToDate) {
                        newConfigDetails[rowId] = {
                            moveToDate: detail.details.moveToDate
                        };
                    } else if (detail.mode === 'exclude') {
                        newConfigDetails[rowId] = {};
                    } else if (detail.mode === 'include') {
                        newConfigDetails[rowId] = {};
                    }
                }
            });

            setActionValues(newActionValues);
            setConfigDetails(newConfigDetails);
        }
    }, [simulation, simulationsList]);

    // Handle create simulation response
    useEffect(() => {
        const { data, isLoading, error } = createSimulation;
        if (data && !isLoading) {
            setIsSaving(false);
            showSuccess('Simulation created successfully!');
            // Reset form
            setIsRenaming(false);
            setRenameValue('');
            setActionValues({});
            setConfigDetails({});
            setSaveMode('new');
            setSimulation('blank simulation');
            // Call callback with created simulation
            if (data.id && data.name) {
                onSimulationSelect?.({ id: String(data.id), name: data.name, simulationDetails: data.simulationDetails });
            }
            // Close modal after brief delay
            setTimeout(() => {
                onClose();
            }, 1500);
        }
        if (error) {
            setIsSaving(false);
            const errorMessage = typeof error === 'string' ? error : error instanceof AxiosError ? error.message : 'Failed to create simulation';
            showError(errorMessage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createSimulation]);

    // Handle update simulation response
    useEffect(() => {
        const { data, isLoading, error } = updateSimulation;
        if (data && !isLoading) {
            setIsSaving(false);
            showSuccess('Simulation updated successfully!');
            
            // Refresh simulations list in all cases (rename or full update)
            if (user?.entityId) {
                const { url: simulationsUrl, method: simulationsMethod } = buildApiRequestConfig(routes.simulation.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
                setFetchSimulations({ url: simulationsUrl, method: simulationsMethod });
            }
            
            // Call parent callback to refresh simulations in parent
            onSimulationsUpdated?.();
            
            // If we were in rename mode, just close rename
            if (isRenaming) {
                setIsRenaming(false);
            } else {
                // For full save/update, use fresh data from API response (not stale simulationsList)
                if (data.id && data.name && data.simulationDetails) {
                    onSimulationSelect?.({ id: String(data.id), name: data.name, simulationDetails: data.simulationDetails });
                }
                // Reset form
                setRenameValue('');
                setActionValues({});
                setConfigDetails({});
                setSaveMode('new');
                setSimulation('blank simulation');
                // Close modal after brief delay
                setTimeout(() => {
                    onClose();
                }, 1500);
            }
        }
        if (error) {
            setIsSaving(false);
            const errorMessage = typeof error === 'string' ? error : error instanceof AxiosError ? error.message : 'Failed to update simulation';
            showError(errorMessage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateSimulation]);

    // Handle delete simulation response
    useEffect(() => {
        const { data, isLoading, error } = deleteSimulation;
        if (data && !isLoading) {
            showSuccess('Simulation deleted successfully!');
            setOpenDeletePopup(false);
            // Call the callback to notify parent to close simulation detail modal
            if (simulationToDelete && onSimulationDeleted) {
                onSimulationDeleted(simulationToDelete);
            }
            // If the deleted simulation was selected, reset to blank
            if (String(simulationToDelete) === String(simulation)) {
                setSimulation('blank simulation');
                const today = new Date();
                const day = String(today.getDate()).padStart(2, '0');
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const year = today.getFullYear();
                setRenameValue(`sim-${day}${month}${year}`);
                setActionValues({});
                setConfigDetails({});
                setSaveMode('new');
            }
            setSimulationToDelete(null);
            // Refresh simulations list
            if (user?.entityId) {
                const { url: simulationsUrl, method: simulationsMethod } = buildApiRequestConfig(routes.simulation.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
                setFetchSimulations({ url: simulationsUrl, method: simulationsMethod });
            }
        }
        if (error) {
            const errorMessage = typeof error === 'string' ? error : error instanceof AxiosError ? error.message : 'Failed to delete simulation';
            showError(errorMessage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteSimulation]);

    // Handle rename simulation response - now handled by updateSimulation hook
    useEffect(() => {
        const { data, isLoading, error } = renameSimulation;
        if (data && !isLoading) {
            setIsRenaming(false);
            showSuccess('Simulation renamed successfully!');
            if (user?.entityId) {
                const { url: simulationsUrl, method: simulationsMethod } = buildApiRequestConfig(routes.simulation.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
                setFetchSimulations({ url: simulationsUrl, method: simulationsMethod });
            }
        }
        if (error) {
            const errorMessage = typeof error === 'string' ? error : error instanceof AxiosError ? error.message : 'Failed to rename simulation';
            showError(errorMessage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renameSimulation]);

    // Save favorites to API whenever favorites state changes
    useEffect(() => {
        if (!open || favorites.size === 0 || !user?.guid || !user?.entityId) {
            return;
        }

        const { url, method } = buildApiRequestConfig(routes.filter.saveFavorites, [{ param: 'userGuid', value: user.guid }]);
        setSaveFavorites({ 
            url, 
            method,
            body: {
                favoriteSimIds: Array.from(favorites),
                entityId: user.entityId
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favorites, open, user?.guid, user?.entityId]);

    // Handle save favorites response
    useEffect(() => {
        const { data, isLoading, error } = saveFavorites;
        if (data && !isLoading) {
            // Favorites saved successfully, notify parent with updated favorites
            const updatedFavorites = Array.from(favorites)
                .map(favId => {
                    const sim = simulationsList.find(s => String(s.id) === String(favId));
                    return sim ? { id: String(sim.id), name: sim.name } : null;
                })
                .filter((sim): sim is { id: string; name: string } => sim !== null);
            onFavoritesUpdated?.(updatedFavorites);
        }
        if (error) {
            const errorMessage = typeof error === 'string' ? error : error instanceof AxiosError ? error.message : 'Failed to save favorites';
            showError(errorMessage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveFavorites]);

    const handleActionChange = (rowId: string, value: string) => {
        setActionValues(prev => ({ ...prev, [rowId]: value }));
    };

    const handleConfigure = (rowId: string, mode: 'defer' | 'move' | 'exclude-between') => {
        setSelectedMode(mode);
        setSelectedRowId(rowId);
        
        // Find and set provision data if this is a provision row
        const provision = provisionList.find(p => String(p.id) === rowId);
        setSelectedProvisionData(provision);
        
        // For real-transactions rows, extract transactions from summaryData
        let transactions: any[] = [];
        if (rowId.startsWith('real-') && summaryData?.realTransactionsByBank) {
            const bankAccountId = rowId.replace('real-', '');
            const bankEntry = Object.entries(summaryData.realTransactionsByBank).find(
                ([_, bankData]: [string, any]) => String(bankData.bankAccountId) === bankAccountId
            );
            if (bankEntry) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const [_bankKey, bankData] = bankEntry as [string, any];
                transactions = bankData.transactions?.map((tx: any) => ({
                    id: tx.guid || tx.id,
                    guid: tx.guid,
                    date: tx.date,
                    amount: tx.amount,
                    description: tx.description,
                    categoryName: tx.category?.name,
                    bankAccountName: tx.bankAccount?.accountName,
                })) || [];
            }
        }
        setConfigureTransactions(transactions);
        
        setOpenConfigureModal(true);
    };

    const handleSave = async () => {
        // Validation
        if (!renameValue.trim()) {
            const today = new Date();
            const day = String(today.getDate()).padStart(2, '0');
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const year = today.getFullYear();
            const defaultName = `sim-${day}${month}${year}`;
            setRenameValue(defaultName);
            showError('Simulation name is required - using default');
            return;
        }

        if (!user?.entityId) {
            showError('User entity ID is missing');
            return;
        }

        setIsSaving(true);

        try {
            // Build simulation details from action values - only include non-default modes
            const allSimulationDetails: Array<any> = provisionList.map((p) => ({
                rowId: String(p.id),
                mode: actionValues[String(p.id)] || 'include',
                isActive: true,
                provision: p
            }));

            // Filter out 'include' mode entries and build details object
            const simulationDetails = allSimulationDetails
                .filter(detail => detail.mode !== 'include')
                .map(detail => {
                    const detailObj: any = {
                        mode: detail.mode,
                        details: null,
                        isActive: detail.isActive
                    };

                    const rowConfig = configDetails[detail.rowId] || {};

                    // Helper to convert date to YYYY-MM-DD string
                    const dateToString = (date: any): string => {
                        if (!date) return '';
                        if (typeof date === 'string') return date;
                        if (date instanceof Date) return date.toISOString().split('T')[0];
                        return String(date);
                    };

                    // Build details object based on mode - match ConfigureModal format
                    if (detail.mode === 'exclude-between') {
                        detailObj.details = {
                            id: detail.rowId,
                            start: dateToString(rowConfig.start),
                            end: dateToString(rowConfig.end)
                        };
                    } else if (detail.mode === 'defer') {
                        detailObj.details = {
                            id: detail.rowId,
                        };
                        if (rowConfig.start) {
                            detailObj.details.start = dateToString(rowConfig.start);
                        }
                        if (rowConfig.end) {
                            detailObj.details.end = dateToString(rowConfig.end);
                        }
                        if (rowConfig.defer) {
                            detailObj.details.defer = dateToString(rowConfig.defer);
                        }
                    } else if (detail.mode === 'move') {
                        // ConfigureModal uses movesByRowId
                        detailObj.details = {
                            id: detail.rowId,
                            movesByRowId: rowConfig.movesByRowId || {}
                        };
                    } else if (detail.mode === 'exclude') {
                        detailObj.details = {
                            id: detail.rowId
                        };
                    }

                    return detailObj;
                });

            // Prepare request payload
            const payload = {
                entityId: user.entityId,
                name: renameValue.trim(),
                simulationDetails: simulationDetails
            };

            // Make API request based on save mode
            if (saveMode === 'new') {
                const { url, method } = buildApiRequestConfig(routes.simulation.create, []);
                setCreateSimulation({ 
                    url, 
                    method,
                    body: payload
                });
            } else if (saveMode === 'changes' && simulation) {
                const { url, method } = buildApiRequestConfig(routes.simulation.update, [{ param: 'id', value: simulation }]);
                setUpdateSimulation({ 
                    url, 
                    method,
                    body: payload
                });
            } else {
                throw new Error('Invalid save mode or simulation ID missing');
            }
        } catch (error) {
            setIsSaving(false);
            showError(error instanceof Error ? error.message : 'Failed to save simulation');
        }
    };

    const actionOptions: DropdownOption[] = [
        { value: 'include', label: 'Include' },
        { value: 'exclude', label: 'Exclude' },
        { value: 'exclude-between', label: 'Exclude Between' },
        { value: 'move', label: 'Move' },
        { value: 'defer', label: 'Defer' },
    ];

    const simulationOptions: DropdownOption[] = [
        ...simulationsList.map(sim => ({
            value: String(sim.id),
            label: sim.name
        }))
    ];

    const handleToggleFavorite = (e: React.MouseEvent, simulationId: string) => {
        e.stopPropagation();
        setFavorites(prev => {
            const newFavorites = new Set(prev);
            if (newFavorites.has(simulationId)) {
                newFavorites.delete(simulationId);
            } else {
                // Only allow up to 4 favorites
                if (newFavorites.size < 4) {
                    newFavorites.add(simulationId);
                } else {
                    showError('You can only favorite up to 4 simulations');
                }
            }
            return newFavorites;
        });
    };

    const handleCreateNewSimulation = (mode: 'sync' | 'include-all' | 'copy', copyFromSimId?: string) => {
        // Clear all modifications and reset to blank simulation
        setSimulation('blank simulation');
        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const year = today.getFullYear();
        setRenameValue(`sim-${day}${month}${year}`);
        setConfigDetails({});
        setSaveMode('new');
        setIsRenaming(false);
        setNewSimulationMenuAnchor(null);
        setNewSimulationMode(mode);

        if (mode === 'sync') {
            // Sync mode: let the effect apply exclusions based on toggles
            setActionValues({});
        } else if (mode === 'include-all') {
            // Include-all mode: include everything (no exclusions from effect)
            setActionValues({});
        } else if (mode === 'copy' && copyFromSimId) {
            // Copy mode: load the simulation's config
            const selectedSimulation = simulationsList.find(sim => String(sim.id) === String(copyFromSimId));
            if (selectedSimulation) {
                const newActionValues: Record<string, string> = {};
                const newConfigDetails: Record<string, any> = {};

                selectedSimulation.simulationDetails?.forEach((detail: any) => {
                    const rowId = detail.details?.id;
                    if (rowId) {
                        newActionValues[rowId] = detail.mode;

                        if (detail.mode === 'exclude-between' && detail.details?.start && detail.details?.end) {
                            newConfigDetails[rowId] = {
                                start: detail.details.start,
                                end: detail.details.end
                            };
                        } else if (detail.mode === 'defer') {
                            newConfigDetails[rowId] = {};
                            if (detail.details?.start && detail.details?.end) {
                                newConfigDetails[rowId].start = detail.details.start;
                                newConfigDetails[rowId].end = detail.details.end;
                            }
                            if (detail.details?.defer) {
                                newConfigDetails[rowId].defer = detail.details.defer;
                            }
                        } else if (detail.mode === 'move' && detail.details?.movesByRowId) {
                            newConfigDetails[rowId] = {
                                movesByRowId: detail.details.movesByRowId
                            };
                        } else if (detail.mode === 'move' && detail.details?.moveToDate) {
                            newConfigDetails[rowId] = {
                                moveToDate: detail.details.moveToDate
                            };
                        } else if (detail.mode === 'exclude') {
                            newConfigDetails[rowId] = {};
                        } else if (detail.mode === 'include') {
                            newConfigDetails[rowId] = {};
                        }
                    }
                });

                setActionValues(newActionValues);
                setConfigDetails(newConfigDetails);
            }
        }
    };

    const renderSimulationOption = (option: DropdownOption, index: number) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
            <Typography variant="body2">{option.label}</Typography>
            <IconButton
                size="small"
                onClick={(e) => handleToggleFavorite(e, option.value)}
                sx={{ padding: '2px', marginLeft: 'auto' }}
            >
                {favorites.has(option.value) ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="var(--color-primary)" stroke="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-star-icon lucide-star"><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"/></svg>
                ) : (
                    <Star size={18} color="#ccc" />
                )}
            </IconButton>
        </Box>
    );

    const columns: Column[] = [
        {
            id: 'transactions',
            label: 'Transactions',
            size: 150,
            type: 'custom',
            onSort: handleTableSort,
            customHeader: renderSortableTableHeader,
            customElement: (value: string, row: Record<string, any>) => (
                <Typography variant="body2" sx={{ fontWeight: (row.id.startsWith('real-') || row.id === 'invoices') ? 'bold' : 400, color: (!row.id.startsWith('real-') && row.id !== 'invoices') ? 'var(--color-provision-text)' : 'inherit' }}>
                    {value}
                </Typography>
            )
        },
        {
            id: 'value',
            label: 'Value',
            size: 80,
            type: 'currency',
            onSort: handleTableSort,
            customHeader: renderSortableTableHeader,
        },
        {
            id: 'recurrence',
            label: 'Recurrence',
            size: 80,
            type: 'string',
            onSort: handleTableSort,
            customHeader: renderSortableTableHeader,
        },
        {
            id: 'date',
            label: 'Date',
            size: 140,
            type: 'string',
            onSort: handleTableSort,
            customHeader: renderSortableTableHeader,
        },
        {
            id: 'action',
            label: 'Action',
            size: 150,
            type: 'custom',
            customElement: (value: string, row: Record<string, any>) => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, boxSizing: 'border-box' }}>
                    <Dropdown
                        options={actionOptions.filter(option => {
                            // Real transactions and invoices only allow include/exclude/exclude-between
                            if (row.id.startsWith('real-') || row.id === 'invoices') {
                                return option.value === 'include' || option.value === 'exclude' || option.value === 'exclude-between';
                            }
                            // Consolidated one-time provisions only allow include/exclude/exclude-between (like invoices)
                            if (row.id === 'onetime-consolidated') {
                                return option.value === 'include' || option.value === 'exclude' || option.value === 'exclude-between';
                            }
                            // Individual one-time provisions only allow include/exclude/move
                            if (row.id.startsWith('onetime-')) {
                                return option.value === 'include' || option.value === 'exclude' || option.value === 'move';
                            }
                            // Recurring provisions allow all options
                            return true;
                        })}
                        value={actionValues[row.id] || value}
                        onChange={(newValue) => handleActionChange(row.id, newValue)}
                        height={25}
                        width={150}
                        InputProps={{
                            style: {
                                fontSize: 12,
                            }
                        }}
                    />
                    <IconButton
                        size="small"
                        disabled={actionValues[row.id] === 'include' || actionValues[row.id] === 'exclude' || !actionValues[row.id]}
                        onClick={() => handleConfigure(row.id, actionValues[row.id] as 'defer' | 'move' | 'exclude-between')}
                        sx={{ padding: '4px', color: (actionValues[row.id] === 'include' || actionValues[row.id] === 'exclude' || !actionValues[row.id]) ? '#ccc' : 'var(--color-primary)' }}
                    >
                        <Settings size={18} />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <>
        <Modal
            open={open}
            onClose={onClose}
            BackdropProps={{ onClick: onClose }}
            sx={isMobile ? { display: 'flex', alignItems: 'flex-end', justifyContent: 'center' } : undefined}
        >
            <Box sx={isMobile ? {
                position: 'relative' as const,
                width: '100%',
                maxHeight: '90vh',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 2,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                display: 'flex',
                flexDirection: 'column' as const,
                boxSizing: 'border-box' as const,
            } : modalStyle}>
                <Box sx={headerStyle}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Select Simulation
                    </Typography>
                    {isRenaming ? (
                        <>
                        <TextField
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            size='small'
                            sx={{ width: 250, fontSize: 18 }}
                        />
                        <Tooltip title="Done" arrow>
                            <IconButton onClick={() => setIsRenaming(false)} size="small">
                                <Save size={18} color='var(--color-primary)'/>
                            </IconButton>
                        </Tooltip>
                        </>
                    ) : (
                        <>
                        <Dropdown
                            options={simulationOptions.map(opt => {
                                // If this is the currently selected simulation and has been renamed, show the new name
                                if (opt.value === simulation && simulation !== 'blank simulation' && renameValue !== simulationsList.find(sim => String(sim.id) === String(simulation))?.name) {
                                    return { ...opt, label: renameValue };
                                }
                                return opt;
                            })}
                            value={simulation}
                            onChange={(newValue) => {
                                setSimulation(newValue);
                                // If switching to blank simulation, reset save mode and set default name
                                if (newValue === 'blank simulation') {
                                    setSaveMode('new');
                                    const today = new Date();
                                    const day = String(today.getDate()).padStart(2, '0');
                                    const month = String(today.getMonth() + 1).padStart(2, '0');
                                    const year = today.getFullYear();
                                    setRenameValue(`sim-${day}${month}${year}`);
                                    setActionValues({});
                                    setConfigDetails({});
                                }
                            }}
                            height={34}
                            width={250}
                            renderOption={renderSimulationOption}
                        />
                        <Tooltip title="Create New Simulation" arrow>
                            <IconButton
                                onClick={(e) => setNewSimulationMenuAnchor(e.currentTarget)}
                                size="small"
                                sx={{ color: 'var(--color-primary)' }}
                            >
                                <SquarePlus size={18} />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={newSimulationMenuAnchor}
                            open={Boolean(newSimulationMenuAnchor)}
                            onClose={() => {
                                setNewSimulationMenuAnchor(null);
                                setCopySimulationMenuAnchor(null);
                            }}
                        >
                            <MenuItem onClick={() => {
                                handleCreateNewSimulation('sync');
                                setNewSimulationMenuAnchor(null);
                            }}>
                                Sync with Toggles
                            </MenuItem>
                            <MenuItem onClick={() => {
                                handleCreateNewSimulation('include-all');
                                setNewSimulationMenuAnchor(null);
                            }}>
                                Include All
                            </MenuItem>
                            <MenuItem 
                                onClick={(e) => setCopySimulationMenuAnchor(e.currentTarget)}
                            >
                                Copy Simulation
                            </MenuItem>
                        </Menu>
                        <Menu
                            anchorEl={copySimulationMenuAnchor}
                            open={Boolean(copySimulationMenuAnchor)}
                            onClose={() => setCopySimulationMenuAnchor(null)}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left'
                            }}
                        >
                            {simulationsList.map((sim) => (
                                <MenuItem 
                                    key={sim.id}
                                    onClick={() => {
                                        handleCreateNewSimulation('copy', String(sim.id));
                                        setNewSimulationMenuAnchor(null);
                                        setCopySimulationMenuAnchor(null);
                                    }}
                                >
                                    {sim.name}
                                </MenuItem>
                            ))}
                        </Menu>
                        <Tooltip title="Rename" arrow>
                            <IconButton 
                                onClick={() => {setIsRenaming(true); setRenameValue(simulationOptions.find(option => option.value === simulation)?.label || '')}} 
                                disabled={simulation === 'blank simulation'}
                                size="small">
                                <Edit size={18} color={simulation === 'blank simulation' ? '#ccc' : 'var(--color-primary)'}/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Simulation" arrow>
                            <IconButton 
                                onClick={() => {setOpenDeletePopup(true); setSimulationToDelete(simulation);}}
                                disabled={simulation === 'blank simulation'}
                                size="small">
                                <Trash size={18} color={simulation === 'blank simulation' ? '#ccc' : '#b60000'}/>
                            </IconButton>
                        </Tooltip>
                        </>
                    )}
                    </Box>
                {(() => {
                const tableData = provisionList.map((p) => {
                        const rowId = String(p.id);
                        const action = actionValues[rowId];
                        const config = configDetails[rowId];
                        const ownDateRange = () => p.type === 'one-time' && p.transactionDate
                            ? formatDate(p.transactionDate)
                            : (p.startDate && !p.endDate) ? `${formatDate(p.startDate)} onwards`
                            : (p.startDate && p.endDate ? `${formatDate(p.startDate)} to ${formatDate(p.endDate)}` : '-');
                        const addDays = (dateStr: string, delta: number): string => {
                            const d = new Date(dateStr);
                            d.setDate(d.getDate() + delta);
                            return d.toISOString().split('T')[0];
                        };
                        let date: string;
                        if (action === 'exclude-between' && config?.start && config?.end) {
                            // config.start/end is the *excluded* window — clip it against the
                            // item's own range so the Date column reflects what's actually still
                            // included, not the raw excluded window (and not the unfiltered full
                            // range when the excluded window covers everything).
                            const itemStartStr = p.type === 'one-time' ? p.transactionDate : p.startDate;
                            const itemEndStr = p.type === 'one-time' ? p.transactionDate : p.endDate;
                            if (itemStartStr && itemEndStr) {
                                const itemStart = new Date(itemStartStr);
                                const itemEnd = new Date(itemEndStr);
                                const exStart = new Date(config.start);
                                const exEnd = new Date(config.end);
                                const noOverlap = exEnd < itemStart || exStart > itemEnd;
                                if (noOverlap) {
                                    date = ownDateRange();
                                } else if (exStart <= itemStart && exEnd >= itemEnd) {
                                    date = 'Excluded';
                                } else if (exStart <= itemStart && exEnd < itemEnd) {
                                    date = `${formatDate(addDays(config.end, 1))} to ${formatDate(itemEndStr)}`;
                                } else if (exStart > itemStart && exEnd >= itemEnd) {
                                    date = `${formatDate(itemStartStr)} to ${formatDate(addDays(config.start, -1))}`;
                                } else {
                                    // Excluded window sits in the middle — representing two
                                    // disjoint remaining segments in one string isn't practical,
                                    // so fall back to the full range.
                                    date = ownDateRange();
                                }
                            } else {
                                date = ownDateRange();
                            }
                        } else if (action === 'defer' && config?.start && config?.end) {
                            date = `${formatDate(config.start)} to ${formatDate(config.end)}`;
                        } else if (action === 'exclude') {
                            // Fully excluded — showing the item's own date range here read as
                            // if it were still in effect for that period, which is misleading.
                            date = 'Excluded';
                        } else {
                            date = ownDateRange();
                        }
                        return {
                            id: rowId,
                            transactions: p.description,
                            value: p.amount,
                            recurrence: p.type,
                            date,
                            // The displayed `date` is a formatted, sometimes
                            // range/word string ("Excluded", "X onwards") -
                            // not sortable as-is. This raw underlying date
                            // (empty string sorts first/last consistently)
                            // is what the Date column actually sorts by.
                            dateSortValue: (p.type === 'one-time' && p.transactionDate ? p.transactionDate : p.startDate) || '',
                            action: action || 'include',
                            detail: '',
                        };
                    });

                    if (tableSort.column) {
                        const dir = tableSort.direction === 'ASC' ? 1 : -1;
                        tableData.sort((a, b) => {
                            if (tableSort.column === 'value') return (a.value - b.value) * dir;
                            if (tableSort.column === 'date') return a.dateSortValue.localeCompare(b.dateSortValue) * dir;
                            const aVal = String((a as any)[tableSort.column] ?? '');
                            const bVal = String((b as any)[tableSort.column] ?? '');
                            return aVal.localeCompare(bVal) * dir;
                        });
                    }

                return isMobile ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                        {tableData.map((row) => {
                            const isRealOrInvoice = row.id.startsWith('real-') || row.id === 'invoices';
                            const summary = [row.recurrence !== '-' ? row.recurrence : null, row.date !== '-' ? row.date : null].filter(Boolean).join(' · ');
                            return (
                                <Paper key={row.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                        <Typography sx={{ fontSize: 13, fontWeight: isRealOrInvoice ? 700 : 500, color: isRealOrInvoice ? 'inherit' : 'var(--color-provision-text)', minWidth: 0, wordBreak: 'break-word' }}>
                                            {row.transactions}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, flexShrink: 0, color: row.value < 0 ? 'var(--color-negative-amount)' : 'inherit' }}>
                                            {formatAmount?.(row.value)}
                                        </Typography>
                                    </Box>
                                    {summary && (
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', mt: 0.25 }}>
                                            {summary}
                                        </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                        <Dropdown
                                            options={actionOptions.filter(option => {
                                                if (row.id.startsWith('real-') || row.id === 'invoices') {
                                                    return option.value === 'include' || option.value === 'exclude' || option.value === 'exclude-between';
                                                }
                                                if (row.id === 'onetime-consolidated') {
                                                    return option.value === 'include' || option.value === 'exclude' || option.value === 'exclude-between';
                                                }
                                                if (row.id.startsWith('onetime-')) {
                                                    return option.value === 'include' || option.value === 'exclude' || option.value === 'move';
                                                }
                                                return true;
                                            })}
                                            value={actionValues[row.id] || row.action}
                                            onChange={(newValue) => handleActionChange(row.id, newValue)}
                                            height={32}
                                            InputProps={{ style: { fontSize: 12 } }}
                                        />
                                        <IconButton
                                            size="small"
                                            disabled={actionValues[row.id] === 'include' || actionValues[row.id] === 'exclude' || !actionValues[row.id]}
                                            onClick={() => handleConfigure(row.id, actionValues[row.id] as 'defer' | 'move' | 'exclude-between')}
                                            sx={{ padding: '4px', flexShrink: 0, color: (actionValues[row.id] === 'include' || actionValues[row.id] === 'exclude' || !actionValues[row.id]) ? '#ccc' : 'var(--color-primary)' }}
                                        >
                                            <Settings size={18} />
                                        </IconButton>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                ) : (
                    <DynamicTable
                        columns={columns}
                        data={tableData}
                        contained={false}
                        maxHeight={400}
                        sortState={tableSort.column ? tableSort : undefined}
                    />
                );
                })()}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button variant='outlined' onClick={() => { 
                        onClose(); 
                    }} disabled={isSaving}>Cancel</Button>
                    <Button variant='outlined' onClick={() => {
                        if (simulation && simulation !== 'blank simulation') {
                            const selectedSimulation = simulationsList.find(sim => String(sim.id) === String(simulation));
                            if (selectedSimulation) {
                                onSimulationSelect?.({ id: String(selectedSimulation.id), name: selectedSimulation.name, simulationDetails: selectedSimulation.simulationDetails });
                            }
                        }
                        // Reset form state after applying
                        setIsRenaming(false);
                        setRenameValue('');
                        setActionValues({});
                        setConfigDetails({});
                        setSaveMode('new');
                        onClose();
                    }} disabled={isSaving}>Apply</Button>
                    <Button 
                        sx={{ bgcolor: 'var(--color-primary)', color: '#fff', '&:hover': { bgcolor: 'var(--color-primary-hover)' } }}
                        onClick={handleSave}
                        disabled={isSaving || !renameValue.trim() || (saveMode === 'changes' && (!simulation || simulation === 'blank simulation'))}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Box>
        </Modal>
        <DeleteModal
            open={openDeletePopup}
            onConfirm={() => {
                if (simulationToDelete && simulationToDelete !== 'blank simulation' && user?.entityId) {
                    const { url, method } = buildApiRequestConfig(routes.simulation.delete, [{ param: 'id', value: simulationToDelete }]);
                    setDeleteSimulation({ url, method });
                }
            }}
            onCancel={() => {setOpenDeletePopup(false); setSimulationToDelete(null);}}
            message="Delete Simulation"
            description="Are you sure you want to delete this simulation? This action cannot be undone."
        />
        <ConfigureModal 
            open={openConfigureModal} 
            onClose={() => setOpenConfigureModal(false)} 
            selectedRowId={selectedRowId}
            mode={selectedMode}
            group={selectedRowId?.startsWith('real-') ? 'real-transactions' : (selectedRowId === 'invoices' ? 'invoices' : 'provisions')}
            initialData={selectedRowId ? configDetails[selectedRowId] : undefined}
            provisionData={selectedProvisionData}
            transactions={configureTransactions}
            onSave={(config) => {
                if (selectedRowId) {
                    setConfigDetails(prev => ({ ...prev, [selectedRowId]: config }));
                }
            }}
        />
        </>
    );
};

export default SimulationsPopup;
